import {
    Button,
    Classes,
    Divider,
    Icon,
    Intent,
    Menu,
    MenuItem,
    Popover,
    Tag,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import {
    segmentTrackApplyBulkProjectAction,
    segmentTrackChangeProjectFilter,
    segmentTrackDismissErisBelowNrisWarning
} from "in_queue/analytics";
import { useCluster, useRegionConfig } from "in_queue/contexts/ClusterContext";
import { useAllAvailableProjects } from "in_queue/contexts/ProjectDataContext";
import { useClusterProjectRows } from "in_queue/hooks/useClusterProjectRows";
import { isOnlyExclusionSupported } from "in_queue/types/configPerRegion";
import { isWithdrawnByDefault } from "in_queue/types/projectType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getWholeProjectOnlyProjectIds } from "../ScenarioDialog/wholeProjectOnly";
import { SelectionBar } from "../SelectionBar/SelectionBar";
import { AgGridColumnDefs, NiraColDef } from "../tables/agGrid/agGridHelpers";
import { BLUEPRINT_SORT_ICONS } from "../tables/agGrid/sortIcons";
import { BaseAgGrid } from "../tables/base/BaseAgGrid";
import { TableActionRibbon } from "../tables/ribbon/TableActionRibbon";
import {
    ProjectFilterCriteria,
    ProjectFilterToggle,
    useProjectFilters
} from "../tables/ribbon/useProjectFilters";
import { BulkDownsizePopover } from "./BulkDownsizePopover";
import {
    BulkActionRules,
    bulkActionRules,
    BulkProjectAction,
    BULK_ACTION_INFO
} from "./bulkProjectActions";
import { ColumnId, getColumnDefs, PickerContext } from "./projectPickerColumns";
import {
    buildProjectPickerRows,
    isErisBelowNris,
    NRIS_EXCEEDS_ERIS_TARIFF,
    projectPickerPostSort,
    ProjectPickerRow
} from "./projectPickerRow";
import css from "./ProjectPickerTable.module.scss";
import { useCapacityOnlyProjectIds } from "./useCapacityOnlyProjectIds";
import { usePortfolioProjectIds } from "./usePortfolioProjectIds";
import {
    ProjectReductionChange,
    useProjectAssumptionsEditor
} from "./useProjectAssumptionsEditor";

const PICKER_TOGGLES: ReadonlyArray<ProjectFilterToggle<ProjectPickerRow>> = [
    {
        key: "queue",
        label: "Queue",
        options: [
            { value: "current", label: "Current cycle" },
            { value: "prior", label: "Prior queue" }
        ],
        getValue: (row) => (row.isPriorQueue ? "prior" : "current")
    },
    {
        key: "size",
        label: "Size",
        options: [
            { value: "full", label: "Full size" },
            { value: "downsized", label: "Downsized" }
        ],
        getValue: (row) =>
            row.isExcluded
                ? undefined
                : row.reduction === undefined
                ? "full"
                : "downsized"
    }
];

const pickerRowFilterCriteria = (
    row: ProjectPickerRow
): ProjectFilterCriteria => ({
    fuelType: row.project.fuelType,
    studyGroup: row.project.cluster.studyGroup,
    isTracked: row.isTracked,
    stats: row.stats
});

const ROW_HEIGHT = 48;
const HEADER_ROW_HEIGHT = 42;
const GROUP_HEADER_ROW_HEIGHT = 26;
const HEADER_BORDER_AND_SCROLLBAR_HEIGHT = 17;
const GRID_CHROME_HEIGHT =
    HEADER_ROW_HEIGHT +
    GROUP_HEADER_ROW_HEIGHT +
    HEADER_BORDER_AND_SCROLLBAR_HEIGHT;
const GRID_MAX_HEIGHT = 420;
const BULK_EDIT_LABEL = "Bulk edit projects";
const WITHDRAWN_EXCLUDED_TOOLTIP =
    "Withdrawn projects are excluded by default, in the builder and on CSV upload. To include one, restore it from this list";
const selectColumn = (
    rules: BulkActionRules
): NiraColDef<ProjectPickerRow, ColumnId> => ({
    colId: "select",
    headerName: "",
    width: 44,
    pinned: "left",
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    showDisabledCheckboxes: true,
    sortable: false,
    resizable: false,
    suppressMovable: true,
    tooltipValueGetter: ({ data }) =>
        data !== undefined && !rules.isRowSelectable(data)
            ? rules.disabledReason(data)
            : ""
});

const DEFAULT_COL_DEF = {
    resizable: true,
    unSortIcon: true,
    cellStyle: { display: "flex", alignItems: "center" }
};

/**
 * One group's rows in their own bounded, scrollable grid.
 *
 */
const PickerGroupGrid: React.FC<{
    readonly kind: "Included" | "Excluded";
    readonly rows: ReadonlyArray<ProjectPickerRow>;
    readonly columnDefs: AgGridColumnDefs<ProjectPickerRow, ColumnId>;
    readonly context: PickerContext;
    readonly isLoading: boolean;
    readonly filterText: string;
    readonly rowFilter: (row: ProjectPickerRow) => boolean;
    readonly rules?: BulkActionRules;
    readonly gridRef?: RefObject<AgGridReact<ProjectPickerRow>>;
    readonly onSelectionChanged?: () => void;
    readonly warning?: React.ReactNode;
}> = ({
    kind,
    rows,
    columnDefs,
    context,
    isLoading,
    filterText,
    rowFilter,
    rules,
    gridRef,
    onSelectionChanged,
    warning
}) => {
    const [visibleCount, setVisibleCount] = useState<number>();
    const [priorQueueCount, setPriorQueueCount] = useState(0);
    const rowData = useMemo(() => [...rows], [rows]);
    const gridColumnDefs = useMemo(
        () => (rules ? [selectColumn(rules), ...columnDefs] : columnDefs),
        [rules, columnDefs]
    );
    const noneSelectable =
        rules !== undefined &&
        rows.length > 0 &&
        !rows.some((row) => rules.isRowSelectable(row));
    const isFiltered =
        rows.length > 0 &&
        visibleCount !== undefined &&
        visibleCount < rows.length;

    return (
        <div className={css["ProjectPickerTable-group"]}>
            <div className={css["ProjectPickerTable-group-header"]}>
                <span
                    className={classNames(
                        Classes.HEADING,
                        css["ProjectPickerTable-group-label"]
                    )}
                >
                    {kind} projects
                </span>
                <Tag round minimal>
                    {isFiltered
                        ? `${visibleCount.toLocaleString()} of ${rows.length.toLocaleString()}`
                        : rows.length.toLocaleString()}
                </Tag>
                {visibleCount !== undefined && priorQueueCount > 0 && (
                    <span
                        className={classNames(
                            Classes.TEXT_MUTED,
                            Classes.TEXT_SMALL
                        )}
                    >
                        {(visibleCount - priorQueueCount).toLocaleString()}{" "}
                        current cycle · {priorQueueCount.toLocaleString()} prior
                        queue
                    </span>
                )}
                {noneSelectable && (
                    <span
                        className={classNames(
                            Classes.TEXT_MUTED,
                            Classes.TEXT_SMALL
                        )}
                    >
                        not selectable in this action
                    </span>
                )}
                {warning}
            </div>
            {!isLoading && rows.length === 0 ? (
                <div
                    className={classNames(
                        css["ProjectPickerTable-group-empty"],
                        Classes.TEXT_MUTED
                    )}
                >
                    No projects are {kind.toLowerCase()} in this scenario.
                </div>
            ) : (
                <BaseAgGrid<ProjectPickerRow>
                    ref={gridRef}
                    className={css["ProjectPickerTable"]}
                    loadingData={isLoading ? "loading" : rowData}
                    columnDefs={gridColumnDefs}
                    rowSelection="multiple"
                    suppressRowClickSelection
                    suppressCellFocus
                    isRowSelectable={(node) =>
                        node.data !== undefined &&
                        (rules?.isRowSelectable(node.data) ?? false)
                    }
                    onSelectionChanged={onSelectionChanged}
                    defaultColDef={DEFAULT_COL_DEF}
                    icons={BLUEPRINT_SORT_ICONS}
                    context={context}
                    postSortRows={projectPickerPostSort}
                    getRowId={(params) => params.data.project.projectId}
                    height={Math.min(
                        GRID_CHROME_HEIGHT +
                            Math.max(visibleCount ?? rows.length, 1) *
                                ROW_HEIGHT,
                        GRID_MAX_HEIGHT
                    )}
                    rowHeight={ROW_HEIGHT}
                    headerHeight={HEADER_ROW_HEIGHT}
                    groupHeaderHeight={GROUP_HEADER_ROW_HEIGHT}
                    analyticsTableName="Scenario Projects Picker"
                    quickFilterText={filterText}
                    rowFilter={rowFilter}
                    onModelUpdated={(event) => {
                        let priorQueue = 0;
                        event.api.forEachNodeAfterFilter((node) => {
                            if (node.data?.isPriorQueue) priorQueue += 1;
                        });
                        setVisibleCount(event.api.getDisplayedRowCount());
                        setPriorQueueCount(priorQueue);
                    }}
                />
            )}
        </div>
    );
};

export interface ProjectPickerTableProps {
    readonly projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    readonly setProjectSizeAssumptions: (
        assumptions: Record<string, MisoPercentageReduction>
    ) => void;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
    readonly setIncludedSpeculativeProjectIds: (
        ids: ReadonlyArray<string>
    ) => void;
    readonly isErisWarningDismissed: boolean;
    readonly setIsErisWarningDismissed: (dismissed: boolean) => void;
}

export const ProjectPickerTable: React.FC<ProjectPickerTableProps> = ({
    projectSizeAssumptions,
    setProjectSizeAssumptions,
    includedSpeculativeProjectIds,
    setIncludedSpeculativeProjectIds,
    isErisWarningDismissed,
    setIsErisWarningDismissed
}) => {
    const { projectId } = useParams();
    const cluster = useCluster();
    const componentConfig = useRegionConfig();
    const allProjects = useAllAvailableProjects();
    const portfolioProjectIds = usePortfolioProjectIds();
    const { rows: clusterRowsL } = useClusterProjectRows();
    const clusterRows = clusterRowsL === "loading" ? [] : clusterRowsL;

    const onlyExclusionSupported = isOnlyExclusionSupported(cluster);
    const projects = allProjects === "loading" ? [] : allProjects;

    const capacityOnlyProjectIds = useCapacityOnlyProjectIds();

    const wholeProjectOnlyProjectIds = useMemo(
        () => getWholeProjectOnlyProjectIds(allProjects),
        [allProjects]
    );

    const editor = useProjectAssumptionsEditor({
        method: "ui_project_table",
        projectSizeAssumptions,
        setProjectSizeAssumptions,
        includedSpeculativeProjectIds,
        setIncludedSpeculativeProjectIds,
        capacityOnlyProjectIds
    });

    const rows = useMemo(
        () =>
            buildProjectPickerRows({
                allProjects: projects,
                clusterRows,
                selection: {
                    projectSizeAssumptions,
                    includedSpeculativeProjectIds
                },
                currentProjectId: projectId ?? "",
                portfolioProjectIds,
                currentCluster: cluster
            }),
        [
            projects,
            clusterRows,
            projectSizeAssumptions,
            includedSpeculativeProjectIds,
            projectId,
            portfolioProjectIds,
            cluster
        ]
    );

    const showQueue =
        new Set(projects.map((project) => project.cluster.studyCycle)).size > 1;
    const showSubregion =
        new Set(projects.map((project) => project.cluster.studyGroup)).size > 1;

    const columnDefs = useMemo(
        () =>
            getColumnDefs({
                energyLabel: componentConfig.sizeLabel.energyLabel,
                capacityLabel: componentConfig.sizeLabel.capacityLabel,
                showReduction: !onlyExclusionSupported,
                cluster,
                showSubregion,
                showQueue
            }),
        [
            componentConfig,
            onlyExclusionSupported,
            cluster,
            showSubregion,
            showQueue
        ]
    );

    const gridContext: PickerContext = {
        editor,
        capacityOnlyProjectIds,
        wholeProjectOnlyProjectIds
    };

    const isLoading = allProjects === "loading";

    const includedRows = useMemo(
        () => rows.filter((row) => !row.isExcluded),
        [rows]
    );
    const excludedRows = useMemo(
        () => rows.filter((row) => row.isExcluded),
        [rows]
    );
    const erisBelowNrisCount = componentConfig.strictSizeRulesEnabled
        ? includedRows.filter((row) =>
              isErisBelowNris(row, {}, capacityOnlyProjectIds)
          ).length
        : 0;
    const erisWarning = erisBelowNrisCount > 0 && !isErisWarningDismissed && (
        <span className={css["ProjectPickerTable-eris-warning"]}>
            <Icon icon={IconNames.WARNING_SIGN} size={12} />
            <Tooltip compact content={NRIS_EXCEEDS_ERIS_TARIFF}>
                <span className={Classes.TEXT_SMALL}>
                    {erisBelowNrisCount} with more NRIS MW than ERIS MW
                </span>
            </Tooltip>
            <Button
                icon={<Icon icon={IconNames.CROSS} size={12} />}
                variant="minimal"
                size="small"
                aria-label="Dismiss"
                className={css["ProjectPickerTable-eris-warning-dismiss"]}
                onClick={() => {
                    segmentTrackDismissErisBelowNrisWarning({
                        cluster,
                        numProjects: erisBelowNrisCount
                    });
                    setIsErisWarningDismissed(true);
                }}
            />
        </span>
    );

    const filters = useProjectFilters({
        tableName: "scenario_builder_projects",
        rows: includedRows,
        filterCriteria: pickerRowFilterCriteria,
        region: cluster.region,
        showStudyGroup: showSubregion,
        labels: { studyGroup: "Subregion", tracked: "Portfolio" },
        toggles: {
            label: "Projects",
            untrackedLabel: "Not in portfolio",
            fields: PICKER_TOGGLES
        }
    });

    const [bulkAction, setBulkAction] = useState<BulkProjectAction>();
    const rules = useMemo(
        () =>
            bulkAction === undefined
                ? undefined
                : bulkActionRules(bulkAction, wholeProjectOnlyProjectIds),
        [bulkAction, wholeProjectOnlyProjectIds]
    );
    const includedGridRef = useRef<AgGridReact<ProjectPickerRow>>(null);
    const [selectedRows, setSelectedRows] = useState<
        ReadonlyArray<ProjectPickerRow>
    >([]);
    const syncSelection = () =>
        setSelectedRows(includedGridRef.current?.api?.getSelectedRows() ?? []);
    const clearSelection = () => includedGridRef.current?.api?.deselectAll();
    const selectionBarRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        clearSelection();
        selectionBarRef.current?.scrollIntoView({ block: "nearest" });
    }, [bulkAction]);
    const exitBulk = () => setBulkAction(undefined);
    const applyChanges = (changes: ReadonlyArray<ProjectReductionChange>) => {
        const inView =
            includedGridRef.current?.api?.getDisplayedRowCount() ?? 0;
        const reductionByProjectId = new Map(
            selectedRows.map((row) => [row.project.projectId, row.reduction])
        );
        const excluded = changes.filter((change) => {
            const current = reductionByProjectId.get(change.projectId);
            return (
                (change.eris ?? current?.eris ?? 0) === 100 &&
                (change.nris ?? current?.nris ?? 0) === 100
            );
        }).length;
        editor.setReductions(changes);
        if (bulkAction !== undefined) {
            segmentTrackApplyBulkProjectAction({
                cluster,
                action: bulkAction,
                numProjects: changes.length,
                erisReduction: changes.find((c) => c.eris !== undefined)?.eris,
                nrisReduction: changes.find((c) => c.nris !== undefined)?.nris
            });
        }
        if (excluded >= inView) {
            filters.reset();
        }
        exitBulk();
    };
    const applyUniform = (eris: number, nris: number) =>
        applyChanges(
            selectedRows.map((row) => ({
                projectId: row.project.projectId,
                projectType: row.project.type,
                eris,
                nris
            }))
        );
    const selectedCountLabel = `${selectedRows.length} project${
        selectedRows.length === 1 ? "" : "s"
    }`;

    const bulkLabel =
        bulkAction === undefined
            ? BULK_EDIT_LABEL
            : BULK_ACTION_INFO[bulkAction].activeLabel;
    const bulkMenu = (
        <Popover
            key="bulk"
            placement="bottom-start"
            content={
                <Menu>
                    {Object.values(BulkProjectAction).map((action) => {
                        const { label, description } = BULK_ACTION_INFO[action];
                        return (
                            <MenuItem
                                key={action}
                                active={action === bulkAction}
                                onClick={() =>
                                    setBulkAction(
                                        action === bulkAction
                                            ? undefined
                                            : action
                                    )
                                }
                                labelClassName={
                                    css["ProjectPickerTable-bulk-cancel"]
                                }
                                labelElement={
                                    action === bulkAction && (
                                        <Icon icon={IconNames.CROSS} />
                                    )
                                }
                                text={
                                    <span
                                        className={
                                            css["ProjectPickerTable-bulk-item"]
                                        }
                                    >
                                        <span>{label}</span>
                                        <span
                                            className={classNames(
                                                Classes.TEXT_MUTED,
                                                Classes.TEXT_SMALL
                                            )}
                                        >
                                            {description}
                                        </span>
                                    </span>
                                }
                            />
                        );
                    })}
                </Menu>
            }
        >
            <Button
                className={css["ProjectPickerTable-bulk-button"]}
                text={
                    <span className={css["ProjectPickerTable-bulk-label"]}>
                        {[
                            BULK_EDIT_LABEL,
                            ...Object.values(BULK_ACTION_INFO).map(
                                (info) => info.activeLabel
                            )
                        ].map((label) => (
                            <span
                                key={label}
                                className={
                                    css["ProjectPickerTable-bulk-label-option"]
                                }
                                style={{
                                    visibility:
                                        label === bulkLabel
                                            ? "visible"
                                            : "hidden"
                                }}
                            >
                                {label}
                            </span>
                        ))}
                    </span>
                }
                icon={IconNames.EDIT}
                endIcon={IconNames.CHEVRON_DOWN}
                intent={bulkAction === undefined ? "none" : "primary"}
                variant="outlined"
            />
        </Popover>
    );

    return (
        <div className={css["ProjectPickerTable-wrapper"]}>
            <TableActionRibbon
                leftActions={[
                    bulkMenu,
                    <Divider
                        key="divider"
                        className={css["ProjectPickerTable-divider"]}
                    />,
                    ...filters.actions
                ]}
                rightActions={[
                    <Button
                        key="reset"
                        variant="minimal"
                        intent={Intent.PRIMARY}
                        disabled={!filters.hasActiveFilters}
                        onClick={() => {
                            filters.reset();
                            segmentTrackChangeProjectFilter({
                                tableName: "scenario_builder_projects",
                                filterName: "reset",
                                value: ""
                            });
                        }}
                    >
                        Reset filters
                    </Button>
                ]}
            />

            <PickerGroupGrid
                kind="Included"
                rows={includedRows}
                columnDefs={columnDefs}
                context={gridContext}
                isLoading={isLoading}
                filterText={filters.filterText}
                rowFilter={filters.rowFilter}
                rules={rules}
                gridRef={includedGridRef}
                onSelectionChanged={syncSelection}
                warning={erisWarning}
            />
            {bulkAction !== undefined && (
                <SelectionBar
                    ref={selectionBarRef}
                    className={css["ProjectPickerTable-selection-bar"]}
                    itemName="project"
                    selectedCount={selectedRows.length}
                    emptyLabel={`Select projects to ${bulkAction}`}
                    onClear={clearSelection}
                    onCancel={exitBulk}
                >
                    {bulkAction === BulkProjectAction.Exclude && (
                        <Button
                            intent={Intent.PRIMARY}
                            disabled={selectedRows.length === 0}
                            text={`Exclude ${selectedCountLabel}`}
                            onClick={() => applyUniform(100, 100)}
                        />
                    )}
                    {bulkAction === BulkProjectAction.Downsize && (
                        <Popover
                            placement="top-end"
                            content={
                                <BulkDownsizePopover
                                    rows={selectedRows}
                                    capacityOnlyProjectIds={
                                        capacityOnlyProjectIds
                                    }
                                    energyLabel={
                                        componentConfig.sizeLabel.energyLabel
                                    }
                                    capacityLabel={
                                        componentConfig.sizeLabel.capacityLabel
                                    }
                                    onApply={applyChanges}
                                />
                            }
                        >
                            <Button
                                intent={Intent.PRIMARY}
                                disabled={selectedRows.length === 0}
                                text={`Downsize ${selectedCountLabel}`}
                            />
                        </Popover>
                    )}
                </SelectionBar>
            )}
            <PickerGroupGrid
                kind="Excluded"
                rows={excludedRows}
                columnDefs={columnDefs}
                context={gridContext}
                isLoading={isLoading}
                filterText={filters.filterText}
                rowFilter={filters.rowFilter}
                warning={
                    excludedRows.some((row) =>
                        isWithdrawnByDefault(row.project)
                    ) && (
                        <Tooltip
                            compact
                            content={`${WITHDRAWN_EXCLUDED_TOOLTIP}${
                                onlyExclusionSupported
                                    ? "."
                                    : " or give it a reduction other than 100% in the CSV."
                            }`}
                        >
                            <Icon
                                icon={IconNames.INFO_SIGN}
                                size={12}
                                className={classNames(
                                    Classes.TEXT_MUTED,
                                    css["ProjectPickerTable-withdrawn-info"]
                                )}
                                aria-label="About withdrawn projects"
                                tabIndex={0}
                            />
                        </Tooltip>
                    )
                }
            />
        </div>
    );
};
